/**
 * Four Atoms Custom Cart Drawer Controller
 * Handles global AJAX Add to Cart, quantity updates, removal, and automatic drawer slide-in
 */
(function() {
  'use strict';

  function getDrawer() {
    return document.getElementById('cart-drawer');
  }

  function openCartDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.setAttribute('open', '');
    const dialog = drawer.querySelector('dialog');
    if (dialog) {
      dialog.setAttribute('open', '');
      if (typeof dialog.showModal === 'function' && !dialog.open) {
        try { dialog.showModal(); } catch (e) {}
      }
    }
    if (typeof drawer.open === 'function' && !drawer.isOpen) {
      try { drawer.open(); } catch (e) {}
    }
    document.dispatchEvent(new CustomEvent('theme-drawer:open', { bubbles: true }));
  }

  function closeCartDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    if (typeof drawer.close === 'function') {
      try { drawer.close(); } catch (e) {}
    }
    drawer.removeAttribute('open');
    const dialog = drawer.querySelector('dialog');
    if (dialog) {
      dialog.removeAttribute('open');
      if (typeof dialog.close === 'function') {
        try { dialog.close(); } catch (e) {}
      }
    }
    document.querySelector('.page-wrapper')?.classList.remove('page-wrapper--drawer-open');
    document.dispatchEvent(new CustomEvent('theme-drawer:close', { bubbles: true }));
  }

  // Refresh cart drawer HTML and counters via section rendering
  async function refreshCartUI() {
    try {
      const response = await fetch('/?sections=cart-drawer-section,header-actions');
      if (!response.ok) return;
      const data = await response.json();

      // Update Cart Drawer section
      if (data['cart-drawer-section']) {
        const drawerSection = document.getElementById('shopify-section-cart-drawer-section');
        if (drawerSection) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data['cart-drawer-section'], 'text/html');
          const newInner = doc.querySelector('.cart-drawer__inner') || doc.querySelector('cart-drawer-component');
          const currentInner = drawerSection.querySelector('.cart-drawer__inner') || drawerSection.querySelector('cart-drawer-component');
          if (newInner && currentInner) {
            currentInner.innerHTML = newInner.innerHTML;
            currentInner.className = newInner.className;
          } else {
            drawerSection.innerHTML = data['cart-drawer-section'];
          }
        }
      }

      // Update Header Actions / Cart Counter
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      updateCartBubbleCount(cart.item_count);

      // Dispatch global events for theme components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart }, bubbles: true }));
    } catch (err) {
      console.warn('[FourAtomsCart] Refresh error:', err);
    }
  }

  function updateCartBubbleCount(count) {
    const bubbles = document.querySelectorAll('.cart-bubble, .header-actions__cart-icon .cart-bubble, .theme-drawer__badge');
    bubbles.forEach(bubble => {
      const countEl = bubble.querySelector('[data-testid="cart-bubble"], .cart-bubble__text-count') || bubble;
      if (countEl) {
        countEl.textContent = count;
      }
      if (count > 0) {
        bubble.classList.remove('visually-hidden');
        bubble.classList.remove('hidden');
        bubble.style.display = '';
      } else {
        bubble.classList.add('visually-hidden');
      }
    });

    const cartIcons = document.querySelectorAll('cart-icon');
    cartIcons.forEach(icon => {
      if (count > 0) {
        icon.classList.add('header-actions__cart-icon--has-cart');
      } else {
        icon.classList.remove('header-actions__cart-icon--has-cart');
      }
    });
  }

  // Handle Global Add to Cart
  async function handleAddToCart(form, button) {
    const btn = button || form.querySelector('[type="submit"]') || form.querySelector('button');
    const originalText = btn ? btn.innerHTML : '';
    
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('data-loading', 'true');
      btn.innerHTML = '<span class="col-btn-spinner" style="display:inline-block;width:16px;height:16px;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:spin 0.6s linear infinite;vertical-align:middle;margin-right:6px;"></span> Adding...';
    }

    try {
      const formData = new FormData(form);
      if (!formData.has('sections')) {
        formData.append('sections', 'cart-drawer-section,header-actions');
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Could not add to cart');
      }

      const data = await response.json();

      if (btn) {
        btn.innerHTML = '<span>Added ✓</span>';
        setTimeout(() => {
          btn.disabled = false;
          btn.removeAttribute('data-loading');
          btn.innerHTML = originalText;
        }, 1500);
      }

      // Refresh drawer UI and open it
      await refreshCartUI();
      openCartDrawer();

    } catch (error) {
      console.error('[FourAtomsCart] Add to cart error:', error);
      alert(error.message || 'Error adding product to cart.');
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('data-loading');
        btn.innerHTML = originalText;
      }
    }
  }

  // Quantity Change Handler inside Drawer
  async function updateLineQuantity(line, quantity) {
    try {
      const drawer = getDrawer();
      if (drawer) drawer.setAttribute('data-loading', 'true');

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
          sections: 'cart-drawer-section,header-actions'
        })
      });

      if (!response.ok) throw new Error('Could not update cart quantity');
      await refreshCartUI();
    } catch (err) {
      console.error('[FourAtomsCart] Change quantity error:', err);
    } finally {
      const drawer = getDrawer();
      if (drawer) drawer.removeAttribute('data-loading');
    }
  }

  // Global exports for inline triggers and external scripts
  window.FourAtomsCart = {
    open: openCartDrawer,
    close: closeCartDrawer,
    refresh: refreshCartUI
  };
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;

  // Immediate and bulletproof event delegation
  function setupCartEventListeners() {
    if (window._fourAtomsCartListenersInitialized) return;
    window._fourAtomsCartListenersInitialized = true;

    // 1. Intercept all cart add forms
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form && form.matches('form[action*="/cart/add"], .col-product-card__form, #pdpWatermelonForm, #pdpSeabuckthornForm, #pdpSeaBuckthornForm, .pdp-sb-form, #faStickyATCForm, .fa-sticky-atc__form, #faStickyCardForm, .fa-sticky-card__form')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const submitter = e.submitter || form.querySelector('[type="submit"]');
        handleAddToCart(form, submitter);
      }
    }, true);

    // 2. Intercept static ATC buttons, cart triggers, close buttons, and quantity updates
    document.addEventListener('click', (e) => {
      // Cart drawer close button
      const closeBtn = e.target.closest('.theme-drawer__close-button, [on\\:click*="#cart-drawer/close"], [data-drawer-close], [aria-label*="Close cart"], .cart-drawer__header button');
      if (closeBtn && (closeBtn.closest('#cart-drawer') || closeBtn.closest('theme-drawer') || closeBtn.closest('.cart-drawer__header') || closeBtn.closest('.theme-drawer__header'))) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        closeCartDrawer();
        return;
      }

      // Cart drawer trigger
      const cartTrigger = e.target.closest('[data-testid="cart-drawer-trigger"], .cart-drawer-trigger-btn, .header-actions__cart-icon, .header-actions__action--cart');
      if (cartTrigger) {
        e.preventDefault();
        const drawer = getDrawer();
        if (drawer && (drawer.hasAttribute('open') || drawer.isOpen)) {
          closeCartDrawer();
        } else {
          openCartDrawer();
        }
        return;
      }

      // GoKwik & 3rd Party Account Login Integration
      const accountBtn = e.target.closest('.account-button, .gokwik-login-button, [data-gokwik-login]');
      if (accountBtn && !accountBtn.classList.contains('customer-logged-in')) {
        if (typeof window.gokwikOtpLogin === 'function') {
          e.preventDefault();
          window.gokwikOtpLogin();
          return;
        }
        if (typeof window.gokwik?.initLogin === 'function') {
          e.preventDefault();
          window.gokwik.initLogin();
          return;
        }
        if (typeof window.gokwikSdk?.login === 'function') {
          e.preventDefault();
          window.gokwikSdk.login();
          return;
        }
        if (typeof window.gkAccountLogin === 'function') {
          e.preventDefault();
          window.gkAccountLogin();
          return;
        }
      }

      // Standalone Add to Cart buttons without forms
      const atcBtn = e.target.closest('.col-product-card__btn, .pdp-add-to-cart-btn, [data-add-to-cart]');
      if (atcBtn && !atcBtn.closest('form')) {
        e.preventDefault();
        const card = atcBtn.closest('.col-product-card') || atcBtn.closest('.pdp-hero-container') || atcBtn.parentElement;
        const variantInput = card?.querySelector('input[name="id"]');
        const variantId = variantInput ? variantInput.value : atcBtn.getAttribute('data-variant-id');
        
        if (variantId) {
          const fakeForm = document.createElement('form');
          const idInput = document.createElement('input');
          idInput.name = 'id';
          idInput.value = variantId;
          const qtyInput = document.createElement('input');
          qtyInput.name = 'quantity';
          qtyInput.value = '1';
          fakeForm.appendChild(idInput);
          fakeForm.appendChild(qtyInput);
          handleAddToCart(fakeForm, atcBtn);
        }
        return;
      }

      // Quantity Plus/Minus in cart drawer
      const qtyBtn = e.target.closest('#cart-drawer .quantity-plus, #cart-drawer .quantity-minus, #cart-drawer [name="plus"], #cart-drawer [name="minus"]');
      if (qtyBtn) {
        e.preventDefault();
        const row = qtyBtn.closest('.cart-items__table-row');
        const input = row ? row.querySelector('input[type="number"]') : null;
        if (row && input) {
          const line = input.getAttribute('data-cart-line') || (Array.from(row.parentElement.children).indexOf(row) + 1);
          let currentVal = parseInt(input.value, 10) || 1;
          const isPlus = qtyBtn.classList.contains('quantity-plus') || qtyBtn.name === 'plus' || qtyBtn.getAttribute('on:click')?.includes('increase');
          const newVal = isPlus ? currentVal + 1 : Math.max(0, currentVal - 1);
          updateLineQuantity(line, newVal);
        }
        return;
      }

      // Remove button in cart drawer
      const removeBtn = e.target.closest('#cart-drawer .cart-items__remove, #cart-drawer [on\\:click*="onLineItemRemove"]');
      if (removeBtn) {
        e.preventDefault();
        const row = removeBtn.closest('.cart-items__table-row');
        const input = row ? row.querySelector('input[type="number"]') : null;
        if (row) {
          const line = input ? (input.getAttribute('data-cart-line') || (Array.from(row.parentElement.children).indexOf(row) + 1)) : 1;
          updateLineQuantity(line, 0);
        }
        return;
      }
    }, true);

    // Close on backdrop click
    document.addEventListener('click', (e) => {
      const drawer = getDrawer();
      if (!drawer || !drawer.hasAttribute('open')) return;
      const dialog = drawer.querySelector('dialog');
      if (dialog && e.target === dialog) {
        closeCartDrawer();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const drawer = getDrawer();
        if (drawer && (drawer.hasAttribute('open') || drawer.isOpen)) {
          closeCartDrawer();
        }
      }
    });
  }

  // Initialize immediately or on next microtask
  setupCartEventListeners();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCartEventListeners);
  }

  // Inject CSS for spinner animation if needed
  if (!document.getElementById('four-atoms-cart-spinner-style')) {
    const style = document.createElement('style');
    style.id = 'four-atoms-cart-spinner-style';
    style.textContent = '@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }
})();
